import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./api/axios";
import { SearchX } from "lucide-react";
import { useActivityLogger } from "./hooks/useActivity";
import { useAuth } from "./contexts/AuthContext";
import {
  formatTimeInUserTimezone,
  formatDateInUserTimezone,
  getUserTimezone,
} from "./utils/timezoneHelper";

export default function Reminders() {
  const { user, loading: loadingUser } = useAuth();
  const navigate = useNavigate();
  const [rawReminders, setRawReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  // Icon mapping berdasarkan tipe pengingat
  useEffect(() => {
    if (!loadingUser) {
      logUserActivity(
        "user",
        "view_reminders",
        "Reminders",
        "User viewed reminders page"
      );
    }
  }, [loadingUser, userLoading]);

  const iconMap = {
    obat: "/icon-obat.png",
    makan: "/icon-makan.png",
    kontrol: "/icon-dokter.png",
    terapi: "/icon-terapi.png",
  };

  // Label mapping untuk display
  const typeLabels = {
    obat: "Minum obat",
    makan: "Makan",
    kontrol: "Konsultasi dokter",
    terapi: "Terapi",
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Group medicine reminders by medicine name
const groupMedicineReminders = (reminders) => {
  if (!reminders || reminders.length === 0) return [];

  const grouped = {};
  const otherReminders = [];

  reminders.forEach((reminder) => {
    if (reminder.tipe === "obat" && reminder.details?.nama_obat) {
      const key = `${reminder.details.nama_obat}_${reminder.details.frekuensi}`;
      if (!grouped[key]) {
        grouped[key] = {
          ...reminder,
          schedules: [],
          allIds: [],
          allDoneStatuses: [],
          allUpdatedAts: [], // Track all UpdatedAt timestamps
        };
      }

      // Add schedule from this reminder
      if (reminder.details.jadwal && reminder.details.jadwal.length > 0) {
        grouped[key].schedules.push(...reminder.details.jadwal);
      }

      // GUNAKAN ID HURUF BESAR
      if (reminder.ID !== undefined && reminder.ID !== null) {
        grouped[key].allIds.push(reminder.ID);
      }
      grouped[key].allDoneStatuses.push(reminder.is_done);
      
      // Track all UpdatedAt timestamps
      grouped[key].allUpdatedAts.push(reminder.UpdatedAt);

      // Update overall done status (all must be done for group to be done)
      grouped[key].is_done = grouped[key].allDoneStatuses.every(
        (status) => status
      );
      
      // Use the most recent UpdatedAt for sorting
      const mostRecentUpdatedAt = grouped[key].allUpdatedAts.reduce((latest, current) => {
        return new Date(current) > new Date(latest) ? current : latest;
      });
      grouped[key].UpdatedAt = mostRecentUpdatedAt;
    } else {
      otherReminders.push(reminder);
    }
  });

  // Convert grouped object to array and combine with other reminders
  const groupedArray = Object.values(grouped).map((group) => ({
    ...group,
    // Update details to include all schedules
    details: {
      ...group.details,
      jadwal: group.schedules,
    },
  }));

  // Combine and sort by UpdatedAt again to maintain proper order
  const combinedReminders = [...groupedArray, ...otherReminders];
  
  // Re-sort the combined array by UpdatedAt (most recent first)
  return combinedReminders.sort((a, b) => {
    const dateA = new Date(a.UpdatedAt);
    const dateB = new Date(b.UpdatedAt);
    return dateB - dateA; // Descending order (newest first)
  });
};

  const formatReminderDetails = (reminder) => {
    const userTimezone = getUserTimezone();

    switch (reminder.tipe) {
      case "obat":
        if (reminder.details && reminder.details.jadwal) {
          // console.log(reminder.details.jadwal);
          const times = reminder.details.jadwal.map((j) => j.jam_original).join(", ");
          // console.log(times)
          return {
            frequency: reminder.details.frekuensi || "Sesuai jadwal",
            times: times, // Times are already in local format from input
            subtitle: reminder.details.nama_obat,
          };
        }
        break;
      case "makan":
        if (reminder.details && reminder.details.jadwal) {
          const times = reminder.details.jadwal.map((j) => j.jam).join(", ");
          return {
            frequency: reminder.details.frekuensi || "Sesuai jadwal",
            times: times,
            subtitle: null,
          };
        }
        break;
      case "kontrol":
        return {
          frequency: reminder.details?.frekuensi_kontrol || "Sesuai jadwal",
          times: formatDateInUserTimezone(reminder.waktu, userTimezone),
          subtitle: reminder.details?.nama_faskes,
        };
      case "terapi":
        if (reminder.details && reminder.details.jadwal) {
          const scheduleText = reminder.details.jadwal
            .map((j) => `${j.hari}, ${j.jam}`)
            .join("\n");
          return {
            frequency: reminder.details.frekuensi || "Sesuai jadwal",
            times: scheduleText,
            subtitle: reminder.details.tempat_terapi,
          };
        }
        break;
      default:
        return {
          frequency: "Sesuai jadwal",
          times: formatTime(reminder.waktu),
          subtitle: null,
        };
    }

    return {
      frequency: "Sesuai jadwal",
      times: formatTimeInUserTimezone(reminder.waktu, userTimezone),
      subtitle: null,
    };
  };

  useEffect(() => {
    logUserActivity(
      "user",
      "view_reminders",
      "Reminders",
      "User viewed reminders page"
    );
  }, []);

  useEffect(() => {
    const fetchReminders = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build query string for filter
        const queryParams = new URLSearchParams();
        if (filterType) {
          queryParams.append("tipe", filterType);
        }

        const url = `/pengingat${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
        const response = await api.get(url);

        // Sort by UpdatedAt (most recent first)
        const sortedReminders = (response.data || []).sort((a, b) => {
          const dateA = new Date(a.updated_at || a.UpdatedAt);
          const dateB = new Date(b.updated_at || b.UpdatedAt);
          return dateB - dateA; // Descending order (newest first)
        });
        
        setRawReminders(sortedReminders);
      } catch (err) {
        console.error("Failed to fetch reminders:", err);
        setError("Gagal memuat pengingat");
        setRawReminders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReminders();
  }, [filterType]);

  // Filter reminders based on search term
  const filteredRawReminders = rawReminders.filter(
    (reminder) =>
      reminder.judul?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reminder.details?.nama_obat &&
        reminder.details.nama_obat
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (reminder.details?.nama_faskes &&
        reminder.details.nama_faskes
          .toLowerCase()
          .includes(searchTerm.toLowerCase()))
  );

  const filteredReminders = groupMedicineReminders(filteredRawReminders);

  const handleDelete = async (reminder) => {
    // if () {
    try {
      // If it's a grouped medicine reminder, delete all related reminders
      if (reminder.allIds && reminder.allIds.length > 0) {
        await Promise.all(
          reminder.allIds.map((id) => api.delete(`/pengingat/${id}`))
        );
        setRawReminders((prev) =>
          prev.filter((r) => !reminder.allIds.includes(r.ID))
        );
      } else {
        // Single reminder
        await api.delete(`/pengingat/${reminder.ID}`);
        setRawReminders((prev) => prev.filter((r) => r.ID !== reminder.ID));
      }
    } catch (err) {
      console.error("Failed to delete reminder:", err);
      alert("Gagal menghapus pengingat");
    }
    // }
  };

  const handleToggleDone = async (reminder, currentStatus) => {
    try {
      if (reminder.allIds && reminder.allIds.length > 0) {
        console.log(reminder);
        const validIds = reminder.allIds.filter(
          (id) => id !== undefined && id !== null
        );
        await Promise.all(
          validIds.map((id) =>
            api.put(`/pengingat/${id}`, { is_done: !currentStatus })
          )
        );
        setRawReminders((prev) =>
          prev.map((r) =>
            validIds.includes(r.ID) ? { ...r, is_done: !currentStatus } : r
          )
        );
      } else {
        await api.put(`/pengingat/${reminder.ID}`, { is_done: !currentStatus });
        setRawReminders((prev) =>
          prev.map((r) =>
            r.ID === reminder.ID ? { ...r, is_done: !currentStatus } : r
          )
        );
      }
      logUserActivity(
        "user",
        "toggle_reminder_done",
        "Reminders",
        `User ${currentStatus ? "completed" : "uncompleted"} reminder with ID: ${reminder.ID}`
      );
    } catch (err) {
      console.error("Failed to update reminder status:", err);
      alert("Gagal mengubah status pengingat");
    }
  };

  const openDeleteModal = (reminder) => {
    setReminderToDelete(reminder);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setReminderToDelete(null);
  };

  const confirmDelete = async () => {
    if (reminderToDelete) {
      await handleDelete(reminderToDelete);
      closeDeleteModal();
    }
    logUserActivity(
      "user",
      "delete_reminder",
      "Reminders",
      `User deleted reminder with ID: ${reminderToDelete.ID}`
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600">Memuat pengingat...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "user",
                "go_back_from_reminders",
                "Reminders",
                "User navigated back from reminders page"
              );
              navigate("/");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Pengingat</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="text-center mt-20">
          {/* <p className="text-red-600 mb-4">{error}</p> */}
          <p className="text-gray-600 mb-4">
            Anda harus login untuk melihat pengingat.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Masuk
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "user",
                "go_back_from_reminders",
                "Reminders",
                "User navigated back from reminders page"
              );
              navigate("/");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Pengingat</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="text-center mt-20">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg w-80">
            <h2 className="text-lg font-semibold mb-4">Konfirmasi Hapus</h2>
            <p className="mb-6">
              Apakah Anda yakin ingin menghapus pengingat ini?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700"
              >
                Batal
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded bg-red-500 text-white"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="min-h-screen bg-white px-4 py-4 pb-24 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "user",
                "go_back_from_reminders",
                "Reminders",
                "User navigated back from reminders page"
              );
              navigate("/");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Pengingat</h1>
          <div className="h-6 w-6" /> {/* Spacer */}
        </div>

        {/* Search and Filter */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex flex-1 items-center bg-gray-100 rounded-lg px-4 py-2">
            <img src="/icon-search.png" alt="Search" className="h-4 w-4 mr-2" />
            <input
              type="text"
              placeholder="Cari pengingat..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent text-sm focus:outline-none"
            />
          </div>
          <div className="relative">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="appearance-none bg-gray-100 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Semua</option>
              <option value="obat">Obat</option>
              <option value="makan">Makan</option>
              <option value="kontrol">Kontrol</option>
              <option value="terapi">Terapi</option>
            </select>
            <img
              src="/icon-filter.png"
              alt="Filter"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none"
            />
          </div>
        </div>

        {/* Reminder List */}
        {filteredReminders.length > 0 ? (
          <div className="space-y-3">
            {filteredReminders.map((item, idx) => {
              const details = formatReminderDetails(item);
              return (
                <div
                  key={idx}
                  className={`bg-white border rounded-lg p-3 shadow-sm flex justify-between items-start cursor-pointer hover:shadow-md transition-shadow ${
                    item.is_done ? "opacity-60 bg-gray-50" : ""
                  }`}
                  onClick={() =>
                    navigate(`/reminders/edit/${item.ID || item.id}`)
                  }
                >
                  {/* Left Side */}
                  <div className="flex items-start gap-3 flex-1 pointer-events-none">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden relative">
                      <img
                        src={iconMap[item.tipe] || "/icon-default.png"}
                        alt={typeLabels[item.tipe] || item.tipe}
                        className="object-contain w-5 h-5"
                      />
                      {item.is_done && (
                        <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                          <span className="text-green-600 text-xs">✓</span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <p className="text-xs text-blue-600 font-medium mb-1">
                        {typeLabels[item.tipe] || item.tipe}
                      </p>
                      <h2
                        className={`text-sm font-semibold ${
                          item.is_done ? "line-through" : ""
                        }`}
                      >
                        {item.judul}
                      </h2>
                      {details.subtitle && (
                        <p className="text-xs text-gray-500">
                          {details.subtitle}
                        </p>
                      )}

                      {/* Show individual schedule details for medicine */}
                      {item.tipe === "obat" &&
                        item.details?.jadwal &&
                        item.details.jadwal.length > 1 && (
                          <div className="mt-2 text-xs text-gray-600">
                            {item.details.jadwal.map((schedule, index) => (
                              <div key={index} className="flex justify-between">
                                <span>{schedule.jam_original}</span>
                                <span>
                                  {schedule.jumlah} {schedule.satuan}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Right Side */}
                  <div className="flex flex-col items-end text-xs text-gray-500 pointer-events-none">
                    <div className="flex items-center gap-1 mb-1">
                      <img
                        src="/icon-repeat.png"
                        alt="Repeat"
                        className="h-3 w-3"
                      />
                      <span>{details.frequency}</span>
                    </div>
                    <p className="text-right text-gray-700 whitespace-pre-line text-xs">
                      {details.times}
                    </p>

                    {/* Action buttons */}
                    <div className="flex gap-2 mt-2 pointer-events-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card navigation
                          handleToggleDone(item, item.is_done);
                        }}
                        className={`px-2 py-1 rounded text-xs ${
                          item.is_done
                            ? "bg-gray-200 text-gray-600"
                            : "bg-green-100 text-green-600 hover:bg-green-200"
                        }`}
                      >
                        {item.is_done ? "Batal" : "Selesai"}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card navigation
                          openDeleteModal(item);
                        }}
                        className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200"
                      >
                        Hapus
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            {/* <img
              src="/icon-empty.png"
              alt="Empty"
              className="w-16 h-16 mx-auto mb-4 opacity-50"
            /> */}
            <SearchX className="w-16 h-16 mx-auto mb-b opacity-50 m-8" />
            <p className="text-gray-500">
              {searchTerm || filterType
                ? "Tidak ada pengingat yang sesuai"
                : "Belum ada pengingat"}
            </p>
            {!searchTerm && !filterType && (
              <button
                onClick={() => navigate("/add-reminder")}
                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded-lg"
              >
                Tambah Pengingat
              </button>
            )}
          </div>
        )}

        {/* Floating Add Button */}
        <button
          className="fixed bottom-6 right-6 bg-blue-500 text-white w-14 h-14 rounded-full shadow-lg hover:bg-blue-600 transition flex items-center justify-center"
          onClick={() => navigate("/add-reminder")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>
      </div>
    </>
  );
}
